import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Stat, EmptyState } from "../components/common";
import { eur, REJECT_REASONS } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Download } from "lucide-react";

const COLORS = ["#15803D", "#B91C1C", "#6B21A8", "#C2410C"];

export default function Statistiche() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [s, setS] = useState(null);

  const load = async () => {
    const q = new URLSearchParams(); if (from) q.set("date_from", from); if (to) q.set("date_to", to);
    const { data } = await api.get(`/stats/quotes?${q}`); setS(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);
  if (!s) return <p className="text-muted-foreground animate-pulse">Caricamento…</p>;

  const pieData = [
    { name: "Accettati", value: s.n_accepted },
    { name: "Rifiutati", value: s.n_rejected },
    { name: "In attesa/forse", value: s.n_pending },
  ].filter((d) => d.value > 0);

  const reasonData = Object.entries(s.reject_reasons || {}).map(([k, v]) => ({
    name: REJECT_REASONS.find((r) => r.value === k)?.label || k, value: v,
  }));

  const pdfUrl = `${api.defaults.baseURL}/reports/stats.pdf?${new URLSearchParams({ ...(from && { date_from: from }), ...(to && { date_to: to }) })}`;

  return (
    <div>
      <PageHeader title="Statistiche preventivi" subtitle="Diagnostica basata sui dati registrati">
        <a href={pdfUrl} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="btn-pdf"><Download size={16} className="mr-1" /> Report PDF</Button></a>
      </PageHeader>

      <div className="flex gap-3 items-end mb-5 flex-wrap">
        <div><Label className="text-xs">Dal</Label><Input type="date" data-testid="stats-from" value={from} onChange={(e)=>setFrom(e.target.value)} /></div>
        <div><Label className="text-xs">Al</Label><Input type="date" data-testid="stats-to" value={to} onChange={(e)=>setTo(e.target.value)} /></div>
        {(from||to) && <Button variant="ghost" size="sm" onClick={()=>{setFrom("");setTo("");}}>Azzera</Button>}
      </div>

      {s.no_data ? <EmptyState title="Nessun dato" hint="Nessun preventivo inviato nel periodo selezionato." /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat label="Preventivi inviati (unici)" value={s.n_sent} tone="brand" testid="stat-sent" />
            <Stat label="Accettati" value={`${s.n_accepted} (${s.pct_accepted}%)`} tone="green" testid="stat-accepted" />
            <Stat label="Rifiutati" value={`${s.n_rejected} (${s.pct_rejected}%)`} tone="danger" testid="stat-rejected" />
            <Stat label="In attesa / in forse" value={`${s.n_pending} (${s.pct_pending}%)`} testid="stat-pending" />
          </div>

          <div className="card-soft p-4 mb-5 text-sm">
            <p className="font-semibold mb-1">Tasso di accettazione dei preventivi con esito definitivo: <span className="text-brand">{s.acceptance_rate_definitive}%</span></p>
            <p className="text-muted-foreground">Numeratore: {s.numerator_denominator.accepted} accettati · Denominatore: {s.definitive_count} con esito (accettati+rifiutati) · su {s.n_sent} inviati nel periodo · Situazione al {s.period.as_of}</p>
            <p className="text-muted-foreground mt-1">Tempo medio dal primo invio all'accettazione: <b>{s.avg_days_to_accept != null ? `${s.avg_days_to_accept} giorni` : "n/d"}</b> · Accettati diventati lavori: <b>{s.accepted_to_jobs}</b> · Valore inviato {eur(s.value_sent)} · accettato {eur(s.value_accepted)}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Esclusi bozze ({s.n_bozze}), richieste senza preventivo e revisioni. Percentuali calcolate sui preventivi unici inviati, raggruppati per prima data di invio.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="card-soft p-4">
              <h3 className="font-head font-bold mb-3">Distribuzione esiti</h3>
              {pieData.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {pieData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie><Legend /><Tooltip /></PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground">Nessun dato.</p>}
            </div>

            <div className="card-soft p-4">
              <h3 className="font-head font-bold mb-3">Motivi di rifiuto</h3>
              {reasonData.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={reasonData}><XAxis dataKey="name" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Bar dataKey="value" fill="#C2593F" radius={[6,6,0,0]} /></BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-muted-foreground">Nessun rifiuto registrato.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
